import AssetsRetry from 'assets-retry'
import debounce from 'lodash/debounce'

import {
  toMainDomain,
  isWhiteListFileType,
  REPLACE_CDN_DOMAIN,
  REPLACE_MAIN_DOMAIN,
  reportRetry,
  addRetrySentryBreadcrumb,
} from '@/utils/retry-fetch'

// 兜底，防止死循环，在间隔 gap(ms) 内连续触发达 max 次， 则触发 Over
const createContinuousLimit = (gap: number, max: number, onOver?: (key: string, count: number) => void) => {
  const map: Record<string, { count: number; before: number; over: boolean }> = {}

  const debounceRecords: Record<string, any> = {}
  const debounceLog = (key: string, message: string) => {
    clearTimeout(debounceRecords[key])
    debounceRecords[key] = setTimeout(() => {
      console.error(`[limit:${key}]`, message)
    }, 500)
  }

  const isOver = (key: string) => {
    if (map[key]?.over) {
      debounceLog(key, `已再次达上限(${++map[key].count})`)
      onOver?.(key, map[key].count)
      return true
    }
    const now = Date.now()
    const target = map[key] || {
      count: 0,
      before: 0,
      over: false,
    }
    map[key] = target
    // 超过间隔，重置
    if (target.before + gap < now) {
      target.count = 1
      target.before = now
      return false
    }
    target.count++
    target.before = now
    if (target.count > max) {
      target.over = true
      debounceLog(key, `已达上限(${target.count})`)
      onOver?.(key, map[key].count)
      return true
    }
    return false
  }

  isOver.map = map

  return isOver as ((key: string) => boolean) & { map: typeof map }
}

// 每次重试的加载（间隔）时间在 10s 以内产生的连续重试，则判为异常。
// 由于加载资源异常时会触发 GET、HEAD 两次请求，也会导致触发下方 onRetry 2次
// 所以将 max = 3 * 2
const limitIsOver = createContinuousLimit(10000, 6, (key, count) => {
  // 避免图片由于重复渲染导致被认为是重试，而超过阈值导致进行上报
  if (isWhiteListFileType(key)) {
    reportRetry('assets_retry_continuous_limit', { key, count })
  }
})

const cache: Record<string, 1> = {}
const reportAssetsData = debounce(() => {
  const keys = Object.keys(ar).filter((key) => {
    if (key in cache || !ar[key].failed.length) {
      return false
    }
    const isEnd = ar[key].succeeded.length || ar[key].failed.length >= 3
    return isEnd
  })
  keys.forEach((key) => {
    cache[key] = 1
    const retryRes = ar[key]
    const isSuccess = !!retryRes.succeeded.length
    const url = retryRes.failed[0]
    const target = retryRes.succeeded[0] ?? retryRes.failed.slice(-1)[0]
    let count = retryRes.failed.length
    if (isSuccess) {
      count++
    }
    if (url === target) {
      reportRetry(isSuccess ? 'retry-assets-repeat-success' : 'retry-assets-repeat-fail', { url, count })
    } else {
      reportRetry(isSuccess ? 'retry-assets-last-success' : 'retry-assets-last-fail', { url, target })
    }
  })
}, 800)

// https://github.com/Nikaple/assets-retry
const ar = AssetsRetry({
  maxRetryCount: 3,
  domain: [...REPLACE_CDN_DOMAIN, ...REPLACE_MAIN_DOMAIN],
  onRetry(_, originalUrl, retryCollector) {
    // url 若无法匹配 domain，则导致无法记录 retryCollector，将会导致死循环
    if (limitIsOver(originalUrl)) {
      return null
    }
    reportAssetsData()
    const count = retryCollector?.retryTimes || 1
    // 最多 3 次
    if (count >= 3) {
      return null
    }
    // 原链接请求2次，主域请求1次
    const target = count === 2 ? toMainDomain(originalUrl) : originalUrl
    addRetrySentryBreadcrumb('assets', {
      url: originalUrl,
      target,
      curCount: count + 1,
    })
    return target
  },
  onSuccess() {
    reportAssetsData()
  },
  onFail() {
    reportAssetsData()
  },
})
